import { useCallback, useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { DataGridPremium, GridRowClassNameParams, GridSortModel, type GridColumnVisibilityModel, type GridColumnResizeParams } from '@mui/x-data-grid-premium';
import { hiddenFrom } from '../../utils/tracking-helpers';
import { dayBounds } from '../../utils/timezone';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { trackingService, TrackingStatsRow } from '../../services/tracking.service';
import { gridSx, isSundayKey, labelForField, levelDims, apiSortField, dimField, sortForLevel } from './tracking-page.format';
import { ConversionTypeColumn, columnCatalogue, drilldownColumns } from './tracking-page.columns';
import { useColumnPrefs } from '../../hooks/useColumnPrefs';
import { useNotesEditing } from '../../hooks/useNotesEditing';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import { useDrilldownVarNames } from '../../hooks/useDrilldownVarNames';

/** Bounded so the grid keeps its own scroller and the headers pin. Nested levels sit
 *  inside their parent's scroller, so each one gets less room than the last. */
const LEVEL_HEIGHT = [460, 360, 300];

interface Props {
  parent: Record<string, string>;
  childGroupBy: string;
  /** Levels below this one, innermost last. Empty means this is the deepest grid. */
  deeperLevels?: string[];
  depth?: number;
  dateFrom: string;
  dateTo: string;
  timezone: number;
  excludeBots: boolean;
  compare?: boolean;
  convByClick: boolean;
  extraFilters: Record<string, string | string[]>;
  conversionTypeColumns: ConversionTypeColumn[];
  parentSortModel: GridSortModel;
  vNames: Record<string, string>;
}

export default function DrilldownPanel({
  parent, childGroupBy, deeperLevels = [], depth = 0, dateFrom, dateTo, timezone,
  excludeBots, compare, convByClick, extraFilters, conversionTypeColumns, parentSortModel, vNames,
}: Props) {
  // Inherit the parent report's sort so the drilldown matches it (falls back to Visits desc).
  const [sortModel, setSortModel] = useState<GridSortModel>(
    parentSortModel?.length ? sortForLevel(parentSortModel, childGroupBy) : [{ field: 'visits', sort: 'desc' }],
  );
  const drillSortBy = apiSortField(sortModel[0]?.field);
  const drillSortOrder = sortModel[0]?.sort || 'desc';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });

  const sortKey = `${drillSortBy}|${drillSortOrder}`;
  const lastSortKey = useRef(sortKey);
  useEffect(() => {
    if (lastSortKey.current === sortKey) return;
    lastSortKey.current = sortKey;
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [sortKey]);


  const [settled, pendingSettle] = useDebouncedValue({
    parent, childGroupBy, dateFrom, dateTo, timezone,
    excludeBots, compare, convByClick, extraFilters, drillSortBy, drillSortOrder,
    page: paginationModel.page, pageSize: paginationModel.pageSize,
  }, 350);

  const { data, isFetching } = useQuery({
    placeholderData: (prev) => prev,
    queryKey: ['tracking-drilldown', settled],
    queryFn: async () => {
      // Carry the main report's active entity/dimension filters + tags so the drilldown
      // reconciles with the parent row (previously it ignored them entirely).
      const win = {
        ...settled.extraFilters,
        ...dayBounds(settled.dateFrom, settled.dateTo),
        timezone: settled.timezone,
        include_bots: !settled.excludeBots,
        compare: settled.compare,
        conv_time_basis: (settled.convByClick ? 'click' : 'conversion') as 'click' | 'conversion',
      };
      return trackingService.getDrilldown({
        ...win, parent: settled.parent,
        child_group_by: levelDims(settled.childGroupBy),
        sort_by: settled.drillSortBy, sort_order: settled.drillSortOrder,
        limit: settled.pageSize, offset: settled.page * settled.pageSize,
      });
    },
  });

  const childDims = levelDims(childGroupBy);
  const vDim = childDims.find((d) => /^v([1-9]|10)$/.test(d)) ?? '';
  const chain = { ...extraFilters, ...parent };
  const fetched = useDrilldownVarNames(vNames[vDim] ? {} : chain, vDim);
  const names = vNames[vDim] ? vNames : fetched;
  const dimLabel = useCallback((d: string) => (names[d]
    ? `${d.toUpperCase()}: ${names[d]}`
    : labelForField(d)), [names]);
  const childLabel = childDims.map(dimLabel).join(' × ');

  // Honours the report's choice, or a hidden column returns when a row is expanded.
  const comparing = !!compare && (data?.rows?.[0] as any)?.prev_visits !== undefined;
  const notesEditing = useNotesEditing(childGroupBy);
  const allColumns = useMemo(
    () => drilldownColumns({ childGroupBy, childLabel, dimLabel, conversionTypeColumns, compare: comparing,
      costEstimated: (data as any)?.cost_basis === 'estimated', canEditNotes: notesEditing.editable }),
    [childGroupBy, childLabel, dimLabel, conversionTypeColumns, comparing, data, notesEditing.editable],
  );
  const catalogueOrder = useMemo(
    () => columnCatalogue(conversionTypeColumns, childGroupBy).map((c) => c.field),
    [conversionTypeColumns, childGroupBy],
  );
  const { columns: orderedColumns, visibility, save, order } = useColumnPrefs(allColumns, catalogueOrder);
  const { columns, remember: rememberWidth } = useColumnWidths('drilldown', orderedColumns);


  const rows = (data?.rows || []).map((r: TrackingStatsRow, i: number) => ({ id: r.group_key ?? `_${i}`, ...r }));

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    field: string;
    value: string;
    label: string;
  } | null>(null);

  const handleRowContextMenu = useCallback((e: MouseEvent<HTMLElement>) => {
    const id = e.currentTarget.getAttribute('data-id');
    if (!id || id === '__totals__') return;
    const row = rows.find((r) => String(r.id) === id);
    if (!row) return;
    e.preventDefault();
    const cell = (e.target as HTMLElement).closest('[data-field]')?.getAttribute('data-field') ?? '';
    const field = childDims.find((d) => dimField(d) === cell) ?? childDims[0];
    setContextMenu({
      mouseX: e.clientX + 2,
      mouseY: e.clientY - 6,
      field,
      value: String(row.keys?.[field] ?? row.group_key ?? ''),
      label: String(row.names?.[field] ?? row.keys?.[field] ?? row.group_key ?? ''),
    });
  }, [rows, childDims]);

  const handleCloseMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleCopyValue = useCallback(() => {
    if (contextMenu?.value) {
      navigator.clipboard.writeText(contextMenu.value);
    }
    handleCloseMenu();
  }, [contextMenu, handleCloseMenu]);

  // `date` is not a column buildWhere understands: carried down as a filter it would be
  // dropped silently and this level would widen to the whole range. Narrow the window.
  const nextLevel = deeperLevels[0];
  const parentDate = parent.date;
  const parentFilters = useMemo(
    () => Object.fromEntries(Object.entries(parent).filter(([k]) => k !== 'date')), [parent]);
  const getNestedPanel = useCallback((params: any) => {
    if (!nextLevel) return null;
    return (
      <DrilldownPanel
        parent={params.row.keys ?? { [childGroupBy]: String(params.row.group_key ?? '') }}
        childGroupBy={nextLevel}
        deeperLevels={deeperLevels.slice(1)}
        depth={depth + 1}
        dateFrom={parentDate ?? dateFrom}
        dateTo={parentDate ?? dateTo}
        timezone={timezone}
        excludeBots={excludeBots}
        compare={compare}
        convByClick={convByClick}
        extraFilters={{ ...extraFilters, ...parentFilters }}
        conversionTypeColumns={conversionTypeColumns}
        parentSortModel={sortModel}
        vNames={vNames}
      />
    );
  }, [nextLevel, deeperLevels, depth, childGroupBy, parentDate, parentFilters, dateFrom, dateTo,
      timezone, excludeBots, compare, convByClick, extraFilters, conversionTypeColumns, sortModel, vNames]);

  const getNestedHeight = useCallback(() => 'auto' as const, []);

  return (
    <Box sx={{
      p: { xs: 1, md: 2 }, pl: { xs: 1, md: 4 }, bgcolor: 'background.default',
      height: LEVEL_HEIGHT[depth] ?? LEVEL_HEIGHT[LEVEL_HEIGHT.length - 1],
    }}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        onColumnWidthChange={(params: GridColumnResizeParams) => rememberWidth(params.colDef.field, params.width)}
        columnVisibilityModel={visibility}
        onColumnVisibilityModelChange={(m: GridColumnVisibilityModel) => save(order, hiddenFrom(m))}
        loading={isFetching || pendingSettle}
        density="compact"
        rowHeight={comparing ? 54 : undefined}
        sortingMode="server"
        sortingOrder={['desc', 'asc']}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowCount={data?.total_row_count ?? 0}
        pageSizeOptions={[100, 200]}
        disableRowSelectionOnClick
        {...notesEditing.gridProps}
        slotProps={{ row: { onContextMenu: handleRowContextMenu, style: { cursor: 'context-menu' } } }}
        getRowClassName={(params: GridRowClassNameParams) => (childGroupBy === 'date' && isSundayKey(params.row.group_key) ? 'sunday-row' : '')}
        getDetailPanelContent={nextLevel ? getNestedPanel : undefined}
        getDetailPanelHeight={nextLevel ? getNestedHeight : undefined}
        sx={{ ...gridSx, '& .group-name-cell': { fontWeight: 600 } }}
      />

      <Menu
        open={!!contextMenu}
        onClose={handleCloseMenu}
        anchorPosition={contextMenu ? { left: contextMenu.mouseX, top: contextMenu.mouseY } : undefined}
        anchorReference="anchorPosition"
      >
        <MenuItem disabled>{contextMenu?.label}</MenuItem>
        <MenuItem onClick={handleCopyValue}>Copy value</MenuItem>
      </Menu>
    </Box>
  );
}
